import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml } from "./types";

// venueList — a printable numbered wedding list on warm paper inside a
// watercolor floral border. A blue script title, then a single numbered
// column distributed to fill the pin evenly. No photo (matches the checklist).
//   venue.name / title → script title (e.g. "15 Elegant Barn Wedding Ideas")
//   items[]            → numbered list entries
//   brand              → subtle footer line
const CREAM = "#eef0db";
const INK = "#34405c";   // navy item text
const BLUE = "#4a5f86";  // script title + numbers
const LINE = "rgba(74,95,134,.35)";
const BORDER = "https://pins-sauntera.web.app/stock/weddings/list-border.png";

export function venueListTemplate(input: PinInput): string {
  const { brand, typography, venue, items } = input;
  const title = (venue?.name || input.title || "").trim();
  const list = (items ?? []).map((i) => i.name).filter(Boolean).slice(0, 15);
  if (!list.length) throw new Error("venueList template requires items[] with names");

  const tl = title.length;
  const titleSize = tl <= 18 ? 100 : tl <= 26 ? 82 : tl <= 34 ? 70 : tl <= 44 ? 58 : 50;
  const n = list.length;
  const itemFont = n <= 8 ? 36 : n <= 11 ? 31 : n <= 13 ? 27 : 24;
  const rowPad = n <= 8 ? 15 : n <= 11 ? 11 : n <= 13 ? 9 : 7;
  const numW = Math.round(itemFont * 1.5);

  const rowsHtml = list.map((it, i) => `
    <div class="row"><span class="num">${i + 1}</span><span class="txt">${escapeHtml(it)}</span></div>`).join("");

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Pinyon+Script&family=EB+Garamond:wght@400;500;600&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:'EB Garamond',${typography.bodyFont},serif;-webkit-font-smoothing:antialiased;background:${CREAM}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:${CREAM}}
  /* Watercolor floral border (hosted, transparent centre). */
  .border{position:absolute;inset:0;z-index:1;background-image:url('${BORDER}');background-size:cover;background-position:center;background-repeat:no-repeat}
  .wrap{position:absolute;top:118px;bottom:118px;left:128px;right:128px;z-index:2;display:flex;flex-direction:column}
  .title{font-family:'Pinyon Script',cursive;font-size:${titleSize}px;line-height:1.04;color:${BLUE};text-align:center;padding-bottom:.12em;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .tdiv{width:230px;height:1px;background:${LINE};margin:12px auto 0}
  /* Numbered rows — left-aligned in a centred block (numbers align), spread
     evenly to fill the height. */
  .items{flex:1;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;width:fit-content;margin:0 auto}
  .row{display:flex;align-items:baseline;gap:14px;padding:${rowPad}px 0}
  .num{font-family:'EB Garamond',serif;font-weight:600;font-size:${itemFont}px;color:${BLUE};min-width:${numW}px;text-align:right;flex-shrink:0}
  .txt{font-size:${itemFont}px;line-height:1.2;color:${INK}}
  .brandline{text-align:center;font-family:'EB Garamond',serif;font-size:23px;letter-spacing:4px;text-transform:uppercase;color:${BLUE};opacity:.72;padding-top:16px}
</style></head>
<body><div class="pin">
  <div class="border"></div>
  <div class="wrap">
    ${title ? `<div class="title">${escapeHtml(title)}</div><div class="tdiv"></div>` : ""}
    <div class="items">${rowsHtml}</div>
    <div class="brandline">${escapeHtml(brand || "Sauntera Weddings")}</div>
  </div>
</div></body></html>`;
}
